import { useEffect, useId, useRef } from "react"
import { useInView, useMotionValue, useSpring } from "motion/react"
import { cn } from "@/lib/utils"

const DURATION = 0.8
const animatedSet = new Set<string>()

interface SyncedNumberTickerProps {
  value: number
  className?: string
  id?: string
}

export function SyncedNumberTicker({ value, className, id }: SyncedNumberTickerProps) {
  const autoId = useId()
  const key = id ?? autoId
  const skip = animatedSet.has(key)
  const ref = useRef<HTMLSpanElement>(null)
  const motionValue = useMotionValue(skip ? value : 0)
  const springValue = useSpring(motionValue, {
    duration: DURATION * 1000,
    bounce: 0,
  })
  const isInView = useInView(ref, { once: true, margin: "0px" })

  useEffect(() => {
    if (skip) return
    if (isInView) {
      motionValue.set(value)
      animatedSet.add(key)
    }
  }, [motionValue, isInView, value, skip, key])

  useEffect(
    () =>
      springValue.on("change", (latest) => {
        if (ref.current) {
          ref.current.textContent = Intl.NumberFormat("en-US").format(
            Math.round(latest),
          )
        }
      }),
    [springValue],
  )

  const formatted = Intl.NumberFormat("en-US").format(value)

  return (
    <span
      ref={ref}
      className={cn("inline-block tracking-wider tabular-nums", className)}
    >
      {skip ? formatted : "0"}
    </span>
  )
}
